import { useEffect, useState } from "react";
import { richTextToPlain, sanitizeRichText, type StudyDetail } from "@probetalab/contracts";
import { run } from "../lib/api.js";
import { useToast } from "../lib/toast.js";
import { errorMessage } from "../lib/useQuery.js";
import { DocumentField } from "./CaptureDocuments.js";
import { Button, Card } from "./ui.js";

/*
 * Catálogo → Estudio → «Plantilla del informe» (pilot report 2026-11-03 09:03: "¿pudiera ya salir
 * precargado el documento como en Toronja?"). One card per document analyte of the study: the
 * text its Informe editor starts with in Captura, until the chemist saves something. Editing it
 * changes no order and no result; an empty template removes it. Administrador (and support) edit,
 * everyone else reads.
 */

export function DocumentTemplates({ study, admin, onSaved }: { study: StudyDetail; admin: boolean; onSaved: () => void }) {
  const docs = study.analytes.filter((a) => a.is_active && a.kind === "document").sort((a, b) => a.sort_order - b.sort_order);
  if (!docs.length) return null;
  return (
    <>
      {docs.map((a) => (
        <TemplateCard key={a.id} analyteId={a.id} name={a.name} many={docs.length > 1} saved={a.document_template ?? ""} admin={admin} onSaved={onSaved} />
      ))}
    </>
  );
}

function TemplateCard({ analyteId, name, many, saved, admin, onSaved }: { analyteId: string; name: string; many: boolean; saved: string; admin: boolean; onSaved: () => void }) {
  const toast = useToast();
  const [value, setValue] = useState(sanitizeRichText(saved));
  useEffect(() => setValue(sanitizeRichText(saved)), [saved]);
  const [busy, setBusy] = useState(false);
  const dirty = sanitizeRichText(value) !== sanitizeRichText(saved);
  const empty = richTextToPlain(value) === "";

  async function save() {
    setBusy(true);
    try {
      await run("catalogue.document_template.set", { analyte_id: analyteId, template: empty ? "" : value });
      toast.success(empty ? "Plantilla quitada." : "Plantilla del informe guardada.");
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={many ? `Plantilla del informe · ${name}` : "Plantilla del informe"}
      actions={
        admin &&
        dirty && (
          <span className="row" style={{ gap: 8 }}>
            <Button variant="ghost" disabled={busy} onClick={() => setValue(sanitizeRichText(saved))}>
              Descartar
            </Button>
            <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
              Guardar plantilla
            </Button>
          </span>
        )
      }
    >
      <div className="stack" style={{ gap: 10 }}>
        <p className="small muted" style={{ margin: 0 }}>
          En Captura, el informe de este estudio empieza con este texto; el químico lo completa o lo cambia en cada solicitud. Cambiar la plantilla no modifica resultados ya guardados.
        </p>
        <DocumentField label={`Plantilla de ${name}`} value={value} saved={saved} locked={!admin} hint="Toca para escribir la plantilla (por ejemplo: 1. SEDIMENTO URINARIO:)" onChange={setValue} />
      </div>
    </Card>
  );
}
